import { useState } from "react";

function CollapsibleJson({ label, value }: { label: string; value: string }) {
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div style={{ marginBottom: 8 }}>
      <strong
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setCollapsed((c) => !c)}
      >
        {collapsed ? "▶" : "▼"} {label}:
      </strong>
      {!collapsed && (
        <pre
          style={{
            background: "rgba(255,255,255,0.05)",
            padding: "6px",
            borderRadius: "4px",
            marginTop: 4,
            maxHeight: 200,
            overflow: "auto",
          }}
        >
          {value}
        </pre>
      )}
    </div>
  );
}

function DebugButton({
  onClick,
  onClearVisitor,
  store,
}: {
  onClick: () => void;
  onClearVisitor?: () => void;
  store: Record<string, unknown> | null | undefined;
}) {
  // Default to top-right so the debug controls + JSON panel don't overlap the
  // react-scan FPS toolbar, which is pinned to the bottom of the viewport.
  const [corner, setCorner] = useState("top-right");
  const [minimized, setMinimized] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [deviceOverride, setDeviceOverride] = useState<
    "mobile" | "desktop" | null
  >(null);

  const cycleDevice = () => {
    if (deviceOverride === "mobile") setDeviceOverride("desktop");
    else if (deviceOverride === "desktop") setDeviceOverride(null);
    else setDeviceOverride("mobile");
  };

  if (typeof window !== "undefined") {
    (
      window as unknown as { debugDeviceOverride: typeof deviceOverride }
    ).debugDeviceOverride = deviceOverride;
  }

  const getPositionStyle = () =>
    corner === "top-right" ? { top: 20, right: 20 } : { bottom: 20, right: 20 };

  const getDebugPanelPosition = () =>
    corner === "top-right" ? { top: 64, right: 20 } : { bottom: 64, right: 20 };

  const showDebug =
    import.meta.env.VITE_DEBUG === "true" &&
    process.env.NODE_ENV === "development" &&
    !minimized &&
    !hidden;

  if (minimized && !hidden) {
    return (
      <div style={{ position: "fixed", zIndex: 9999, ...getPositionStyle() }}>
        <button
          onClick={() => setMinimized(false)}
          style={{
            padding: "6px",
            background: "gray",
            color: "white",
            border: "none",
            borderRadius: "50%",
          }}
          title="Restore debug panel"
        >
          ⬆️
        </button>
      </div>
    );
  }

  if (hidden) {
    return null;
  }

  // Defensive: safely get values from store
  let tempFilters, filters, apiFilters;
  try {
    tempFilters =
      store && store.tempFilters !== undefined
        ? JSON.stringify(store.tempFilters, null, 2)
        : "N/A";
  } catch {
    tempFilters = "N/A";
  }
  try {
    filters =
      store && store.filters !== undefined
        ? JSON.stringify(store.filters, null, 2)
        : "N/A";
  } catch {
    filters = "N/A";
  }
  try {
    apiFilters =
      store && store.apiFilters !== undefined
        ? JSON.stringify(store.apiFilters, null, 2)
        : "N/A";
  } catch {
    apiFilters = "N/A";
  }

  let scrollY = 0;
  try {
    if (typeof window !== "undefined") {
      scrollY =
        typeof window.scrollY !== "undefined"
          ? window.scrollY
          : window.pageYOffset || 0;
    }
  } catch {
    scrollY = 0;
  }

  return (
    <>
      <div style={{ position: "fixed", zIndex: 9999, ...getPositionStyle() }}>
        <button
          onClick={onClick}
          style={{
            padding: "6px 12px",
            background: "crimson",
            color: "white",
            border: "none",
            borderRadius: "4px",
            marginRight: 8,
          }}
        >
          Clear Storage
        </button>
        {onClearVisitor && (
          <button
            onClick={onClearVisitor}
            style={{
              padding: "6px 12px",
              background: "darkorange",
              color: "white",
              border: "none",
              borderRadius: "4px",
              marginRight: 8,
            }}
            title="Clear visitor_uuid (cookie + IndexedDB) and reload as a new visitor"
          >
            Clear Visitor
          </button>
        )}
        <button
          onClick={cycleDevice}
          style={{
            padding: "6px 12px",
            background: (() => {
              if (deviceOverride === "mobile") return "teal";
              if (deviceOverride === "desktop") return "royalblue";
              return "gray";
            })(),
            color: "white",
            border: "none",
            borderRadius: "4px",
            marginRight: 8,
          }}
          title="Cycle device override: auto/mobile/desktop"
        >
          Device: {deviceOverride || "auto"}
        </button>
        <button
          onClick={() =>
            setCorner(corner === "top-right" ? "bottom-right" : "top-right")
          }
          style={{
            padding: "6px",
            background: "gray",
            color: "white",
            border: "none",
            borderRadius: "50%",
            marginRight: 4,
          }}
          title="Move to other corner"
        >
          ↕️
        </button>
        <button
          onClick={() => setMinimized(true)}
          style={{
            padding: "6px",
            background: "gray",
            color: "white",
            border: "none",
            borderRadius: "50%",
            marginRight: 4,
          }}
          title="Minimize"
        >
          ➖
        </button>
        <button
          onClick={() => setHidden(true)}
          style={{
            padding: "6px",
            background: "black",
            color: "white",
            border: "none",
            borderRadius: "50%",
          }}
          title="Hide debug panel"
        >
          ❌
        </button>
      </div>
      {showDebug && (
        <div
          style={{
            position: "fixed",
            zIndex: 9999,
            ...getDebugPanelPosition(),
            background: "rgba(0,0,0,0.7)",
            color: "white",
            padding: "12px",
            maxWidth: "400px",
            fontSize: "12px",
            whiteSpace: "pre-wrap",
            borderRadius: "6px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
            pointerEvents: "auto", // <-- Now you can interact!
          }}
        >
          <CollapsibleJson label="tempFilters" value={tempFilters} />
          <CollapsibleJson label="filters" value={filters} />
          <CollapsibleJson label="apiFilters" value={apiFilters} />
          <div>
            <strong>scrollY:</strong> {scrollY}
          </div>
          <div>
            <strong>Device override:</strong> {deviceOverride || "auto"}
          </div>
        </div>
      )}
    </>
  );
}

export { DebugButton };
